import jsPDF from 'jspdf'
import 'jspdf-autotable'
import strings from 'strings'

// Structured PDF rather than an html2canvas screenshot of the tab: html2canvas
// is only present as an unpinned alpha transitive dep of jspdf@1.5.3 and
// renders flex / absolutely-positioned layouts (the waterfall bars) poorly.
// Drawing the report gives selectable text, predictable pagination and no
// dependency on that alpha build. Mirrors billingTab.js's jsPDF+autoTable
// approach, which is the established pattern in this codebase.

const MARGIN = 14
const DARK = [21, 36, 32]
const ORANGE = [232, 115, 74]
const PURPLE = [155, 138, 251]
const GREEN = [46, 125, 50]
const MUTED = [150, 150, 150]
const BODY = [45, 45, 45]

const sanitize = (s) => String(s || '').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase()

const formatPercent = (value) => {
  const n = Number(value) || 0
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`
}

export function exportComparisonPdf({
  fromLabel,
  toLabel,
  fromDoc,
  toDoc,
  report,
  verdict,
  fromScore,
  toScore,
  indicatorAnalysis,
  formatCurrency
}) {
  const doc = new jsPDF()
  const pageSize = doc.internal.pageSize
  const pageWidth = pageSize.width || pageSize.getWidth()
  const pageHeight = pageSize.height || pageSize.getHeight()
  const contentWidth = pageWidth - MARGIN * 2
  let y = MARGIN

  const setFontStyle = (style) => {
    if (typeof doc.setFontStyle === 'function') doc.setFontStyle(style)
    else if (typeof doc.setFont === 'function') doc.setFont('helvetica', style)
  }

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - MARGIN) {
      doc.addPage()
      y = MARGIN
    }
  }

  const sectionHeading = (text) => {
    ensureSpace(14)
    doc.setFontSize(12)
    setFontStyle('bold')
    doc.setTextColor(DARK[0], DARK[1], DARK[2])
    doc.text(text, MARGIN, y)
    y += 7
  }

  // Renders a numbered narrative list, paginating line by line.
  const numberedList = (lines) => {
    doc.setFontSize(9)
    setFontStyle('normal')
    doc.setTextColor(BODY[0], BODY[1], BODY[2])
    ;(lines || []).forEach((line, i) => {
      const label = String(i + 1).padStart(2, '0')
      const wrapped = doc.splitTextToSize(String(line), contentWidth - 10)
      ensureSpace(wrapped.length * 5 + 2)
      doc.setTextColor(MUTED[0], MUTED[1], MUTED[2])
      doc.text(label, MARGIN, y)
      doc.setTextColor(BODY[0], BODY[1], BODY[2])
      doc.text(wrapped, MARGIN + 8, y)
      y += wrapped.length * 5 + 2
    })
    y += 4
  }

  const bulletList = (items, color) => {
    doc.setFontSize(9)
    setFontStyle('normal')
    if (!items || items.length === 0) {
      doc.setTextColor(MUTED[0], MUTED[1], MUTED[2])
      ensureSpace(6)
      doc.text('None', MARGIN + 4, y)
      y += 7
      return
    }
    items.forEach((item) => {
      const wrapped = doc.splitTextToSize(String(item), contentWidth - 12)
      ensureSpace(wrapped.length * 5 + 2)
      doc.setFillColor(color[0], color[1], color[2])
      doc.circle(MARGIN + 2, y - 1.2, 1, 'F')
      doc.setTextColor(BODY[0], BODY[1], BODY[2])
      doc.text(wrapped, MARGIN + 7, y)
      y += wrapped.length * 5 + 2
    })
    y += 4
  }

  // ---- Title -------------------------------------------------------------
  doc.setFontSize(16)
  setFontStyle('bold')
  doc.setTextColor(DARK[0], DARK[1], DARK[2])
  doc.text(strings.BANK_STATEMENT_PDF_TITLE, MARGIN, y)
  y += 7

  doc.setFontSize(9)
  setFontStyle('normal')
  doc.setTextColor(MUTED[0], MUTED[1], MUTED[2])
  doc.text(`${fromLabel}  ->  ${toLabel}`, MARGIN, y)
  y += 5
  if (report.comparisonDate) {
    doc.text(`${strings.BANK_STATEMENT_COMPARISON_GENERATED_PREFIX} ${report.comparisonDate}`, MARGIN, y)
    y += 5
  }
  y += 4

  // ---- Verdict banner ----------------------------------------------------
  const headlineLines = doc.splitTextToSize(String(verdict.headline || ''), contentWidth - 46)
  const descLines = doc.splitTextToSize(String(verdict.description || ''), contentWidth - 46)
  const bannerHeight = 16 + headlineLines.length * 7 + descLines.length * 4.6
  ensureSpace(bannerHeight + 6)

  doc.setFillColor(DARK[0], DARK[1], DARK[2])
  doc.rect(MARGIN, y, contentWidth, bannerHeight, 'F')

  let bannerY = y + 8
  doc.setFontSize(8)
  setFontStyle('bold')
  doc.setTextColor(ORANGE[0], ORANGE[1], ORANGE[2])
  doc.text(strings.BANK_STATEMENT_VERDICT_LABEL, MARGIN + 6, bannerY)
  bannerY += 7

  doc.setFontSize(15)
  doc.setTextColor(255, 255, 255)
  doc.text(headlineLines, MARGIN + 6, bannerY)
  bannerY += headlineLines.length * 7

  doc.setFontSize(9)
  setFontStyle('normal')
  doc.setTextColor(215, 215, 215)
  doc.text(descLines, MARGIN + 6, bannerY)

  // Health score pair, right-aligned inside the banner.
  const scoreX = pageWidth - MARGIN - 38
  doc.setFontSize(7)
  setFontStyle('bold')
  doc.setTextColor(160, 160, 160)
  doc.text(strings.BANK_STATEMENT_HEALTH_SCORE, scoreX, y + 8)
  doc.setFontSize(14)
  doc.setTextColor(PURPLE[0], PURPLE[1], PURPLE[2])
  doc.text(`${Number(fromScore).toFixed(1)}`, scoreX, y + 17)
  doc.setTextColor(255, 255, 255)
  doc.text('->', scoreX + 11, y + 17)
  doc.setTextColor(ORANGE[0], ORANGE[1], ORANGE[2])
  doc.text(`${Number(toScore).toFixed(1)}`, scoreX + 22, y + 17)

  y += bannerHeight + 10

  // ---- Metrics table ----------------------------------------------------
  const fromNet = (fromDoc.credits || 0) - (fromDoc.debits || 0)
  const toNet = (toDoc.credits || 0) - (toDoc.debits || 0)
  const endingPct = fromDoc.endBalance
    ? ((toDoc.endBalance - fromDoc.endBalance) / fromDoc.endBalance) * 100
    : 0
  const netReversed = (fromNet >= 0) !== (toNet >= 0)

  const rows = [
    [
      strings.BANK_STATEMENT_STARTING_BALANCE,
      formatCurrency(fromDoc.startingBalance),
      formatCurrency(toDoc.startingBalance),
      '-'
    ],
    [
      strings.BANK_STATEMENT_CREDITS,
      formatCurrency(fromDoc.credits),
      formatCurrency(toDoc.credits),
      formatPercent(report.creditChange?.percentage_change)
    ],
    [
      strings.BANK_STATEMENT_DEBITS,
      formatCurrency(fromDoc.debits),
      formatCurrency(toDoc.debits),
      formatPercent(report.debitChange?.percentage_change)
    ],
    [
      strings.BANK_STATEMENT_NET_CASH_FLOW,
      formatCurrency(fromNet),
      formatCurrency(toNet),
      netReversed ? 'Reversed' : formatPercent(((toNet - fromNet) / (Math.abs(fromNet) || 1)) * 100)
    ],
    [
      strings.BANK_STATEMENT_ENDING_BALANCE,
      formatCurrency(fromDoc.endBalance),
      formatCurrency(toDoc.endBalance),
      formatPercent(endingPct)
    ]
  ]

  const head = ['Metric', fromLabel, toLabel, 'Change']

  sectionHeading(strings.BANK_STATEMENT_PERIOD_OVER_PERIOD)

  if (typeof doc.autoTable === 'function') {
    doc.autoTable({
      head: [head],
      body: rows,
      startY: y,
      margin: { left: MARGIN, right: MARGIN },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: DARK, textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: { 0: { fontStyle: 'bold' } }
    })
    y = (doc.lastAutoTable ? doc.lastAutoTable.finalY : y) + 12
  } else {
    const colWidth = contentWidth / head.length
    const rowHeight = 8

    ensureSpace(rowHeight * (rows.length + 1) + 4)
    doc.setFillColor(DARK[0], DARK[1], DARK[2])
    doc.rect(MARGIN, y, contentWidth, rowHeight, 'F')
    doc.setFontSize(9)
    setFontStyle('bold')
    doc.setTextColor(255, 255, 255)
    head.forEach((cell, i) => {
      doc.text(String(cell), MARGIN + 2 + i * colWidth, y + 5.5)
    })
    y += rowHeight

    rows.forEach((row, rowIndex) => {
      ensureSpace(rowHeight)
      if (rowIndex % 2 === 1) {
        doc.setFillColor(248, 248, 248)
        doc.rect(MARGIN, y, contentWidth, rowHeight, 'F')
      }
      row.forEach((cell, i) => {
        setFontStyle(i === 0 ? 'bold' : 'normal')
        doc.setTextColor(BODY[0], BODY[1], BODY[2])
        doc.text(String(cell), MARGIN + 2 + i * colWidth, y + 5.5)
      })
      y += rowHeight
    })
    y += 12
  }

  // ---- Signal changes ---------------------------------------------------
  sectionHeading(strings.BANK_STATEMENT_WHAT_CHANGED)

  doc.setFontSize(10)
  setFontStyle('bold')
  doc.setTextColor(ORANGE[0], ORANGE[1], ORANGE[2])
  ensureSpace(8)
  doc.text(`${strings.BANK_STATEMENT_NEW} (${(indicatorAnalysis.new_indicators || []).length})`, MARGIN, y)
  y += 6
  bulletList(indicatorAnalysis.new_indicators, ORANGE)

  doc.setFontSize(10)
  setFontStyle('bold')
  doc.setTextColor(PURPLE[0], PURPLE[1], PURPLE[2])
  ensureSpace(8)
  doc.text(`${strings.BANK_STATEMENT_PERSISTENT} (${(indicatorAnalysis.persistent_indicators || []).length})`, MARGIN, y)
  y += 6
  bulletList(indicatorAnalysis.persistent_indicators, PURPLE)

  doc.setFontSize(10)
  setFontStyle('bold')
  doc.setTextColor(GREEN[0], GREEN[1], GREEN[2])
  ensureSpace(8)
  doc.text(`${strings.BANK_STATEMENT_RESOLVED} (${(indicatorAnalysis.resolved_indicators || []).length})`, MARGIN, y)
  y += 6
  bulletList(indicatorAnalysis.resolved_indicators, GREEN)

  const explainer = doc.splitTextToSize(strings.BANK_STATEMENT_RESOLVED_EXPLAINER, contentWidth - 8)
  ensureSpace(explainer.length * 4.6 + 8)
  doc.setFontSize(8)
  setFontStyle('italic')
  doc.setTextColor(MUTED[0], MUTED[1], MUTED[2])
  doc.text(explainer, MARGIN, y)
  y += explainer.length * 4.6 + 8

  // ---- Narrative analysis ------------------------------------------------
  sectionHeading(strings.BANK_STATEMENT_ACCOUNT_STATE_TAB)
  numberedList(report.accountStateAnalysis)

  sectionHeading(strings.BANK_STATEMENT_OVERALL_HEALTH_TAB)
  numberedList(report.overallHealthAnalysis)

  sectionHeading(strings.BANK_STATEMENT_TAKEAWAY_ANALYSIS_TAB)
  numberedList(report.takeAwayAnalysis)

  // ---- Page footers ------------------------------------------------------
  const pageCount = doc.internal.getNumberOfPages
    ? doc.internal.getNumberOfPages()
    : doc.internal.pages.length - 1
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p)
    doc.setFontSize(8)
    setFontStyle('normal')
    doc.setTextColor(MUTED[0], MUTED[1], MUTED[2])
    doc.text(`${p} / ${pageCount}`, pageWidth - MARGIN, pageHeight - 8, { align: 'right' })
  }

  doc.save(`bank-statement-comparison-${sanitize(fromLabel)}-to-${sanitize(toLabel)}.pdf`)
}

export default exportComparisonPdf
